import { createAction, createAsyncThunk } from '@reduxjs/toolkit'
import { handleError, hasError } from '@repo/shared/api/helpers'
import { OnboardingStep } from '@repo/shared/dtos/organization'
import { ApiErrorDTO } from '@repo/shared/error'
import { AsyncData, DataStatus } from '@repo/ui/utils'

import { AppDispatch } from '@/main'
import { IApi as IAltApi } from '@/spi/api'
import { type RootState } from '@/store'
import { Resource } from '@/store/admin'
import { Alert } from '@/store/alert'
import { Beneficiary } from '@/store/beneficiary'
import { getCurrentOrganization } from '@/store/helpers'
import { ISocket } from '@/store/middlewares/types'
import { IAccessTokenManager, IApi } from '@/store/spi/api'
import { ISudoApi } from '@/store/spi/sudo'
import { SudoContextOrganization, SudoContextUser } from '@/store/sudo'
import { assistant } from '@/store/thunks/assistant'
import { beneficiary } from '@/store/thunks/beneficiary'
import { caseWorker } from '@/store/thunks/case-worker'
import { demo } from '@/store/thunks/demo'
import { feedback } from '@/store/thunks/feedback'
import { onboarding } from '@/store/thunks/onboarding'
import { organization } from '@/store/thunks/organization'
import { promptTemplate } from '@/store/thunks/promptTemplate.ts'
import { resource } from '@/store/thunks/resource'
import { snapshot } from '@/store/thunks/snapshot'
import { task } from '@/store/thunks/task'
import { user } from '@/store/thunks/user'
import { CaseWorker, Organization, User } from '@/store/user'

export interface ThunkExtra {
  accessTokenManager: IAccessTokenManager
  api: IApi
  altApi: IAltApi
  sudoApi: ISudoApi
  socket: ISocket
}

export enum RejectValue {
  NoAccess = 'No access token found.',
  NoAlert = 'No alert found.',
  NoBeneficiary = 'No beneficiary found.',
  NoCaseWorker = 'No case worker found.',
  NoChatSession = 'No chat session found.',
  NoChatSessions = 'No chat sessions found.',
  NoGenerationId = 'No generationId found.',
  NoOrganization = 'No organization found.',
  NoResource = 'No resource found.',
  NoSummary = 'No summary found.',
  NoTips = 'No tips found.',
  NoUpdate = 'Failed to update.',
  NoUser = 'No user found.',
  NoContent = 'No content found.',
  NotAllowed = 'Not allowed.',
  NotFilterTemplate = 'Not filter template found.',
}

export type AsyncThunkConfig = {
  extra: ThunkExtra
  dispatch: AppDispatch
  state: RootState
  // TODO: instead of a string provide available errors (e.g. "No organization found.")
  rejectValue: ApiErrorDTO | RejectValue
}

export const sudoStoreActions = {
  initSudo: createAsyncThunk<
    { organizations: SudoContextOrganization[]; users: SudoContextUser[] },
    void,
    AsyncThunkConfig
  >('INIT_SUDO_ADMIN', async (_, { extra, fulfillWithValue, rejectWithValue }) => {
    const { sudoApi } = extra
    const res = await sudoApi.getInitialValues()
    if (hasError(res)) return rejectWithValue(handleError(res))
    return fulfillWithValue(res)
  }),
  organization: organization.sudo,
  user: user.sudo,
  snapshot: snapshot.sudo,
  caseWorker: caseWorker.sudo,
}

export const adminStoreActions = {
  initAnalyticsChat: createAction<{ generationId: string }>('INIT_ANALYTICS_CHAT'),
  initAdmin: createAsyncThunk<
    {
      beneficiaries: Beneficiary[]
      caseWorkers: CaseWorker[]
      resources: Resource[]
    },
    void,
    AsyncThunkConfig
  >('INIT_ADMIN', async (_, { extra, fulfillWithValue, rejectWithValue, getState }) => {
    const { api } = extra
    const organizationId = getState().user.currentOrganizationId
    if (!organizationId) return rejectWithValue(RejectValue.NoOrganization)
    const caseWorker = getState().user.caseWorker
    if (!DataStatus.isFulfilled(caseWorker)) return rejectWithValue(RejectValue.NoCaseWorker)

    const res = await api.admin.getInitialValues({
      caseWorkerId: caseWorker.id,
      organizationId,
    })
    if (hasError(res)) return rejectWithValue(handleError(res))
    return fulfillWithValue(res)
  }),
  onboarding: onboarding.admin,
  caseWorker: caseWorker.admin,
  organization: organization.admin,
  promptTemplate: promptTemplate.admin,
  resource: resource.admin,
  feedback: feedback.admin,
}

export const demoStoreActions = demo.app

export const storeActions = {
  view: {
    beneficiary: {
      tasks: createAction<{ generationId: string }>('VIEW_BENEFICIARY_TASKS'),
      profile: createAction<{ generationId: string }>('VIEW_BENEFICIARY_PROFILE'),
    },
  },
  assistant: assistant.app,
  beneficiary: beneficiary.app,
  caseWorker: caseWorker.app,
  user: user.app,
  initApp: createAsyncThunk<{ organizations: Organization[]; user: User }, void, AsyncThunkConfig>(
    'INIT_APP',
    async (_, { extra, fulfillWithValue, rejectWithValue, getState }) => {
      const { api } = extra
      const state = getState()
      const user = state.user.me
      const organizations = state.user.organizations

      if (DataStatus.isFulfilled(user) && DataStatus.isFulfilled(organizations)) {
        // Cache
        return fulfillWithValue({
          organizations,
          user,
        })
      } else {
        const data = await api.me.getInitialValues()
        if (hasError(data)) return rejectWithValue(handleError(data))
        return fulfillWithValue(data)
      }
    },
  ),
  initDashboard: createAction('INIT_DASHBOARD'),
  cleanUpOrganization: createAction('CLEAN_UP_ORGANIZATION'),
  initOrganization: createAsyncThunk<
    {
      alerts: Alert[]
      beneficiaries: Beneficiary[]
      caseWorker: CaseWorker
      organization: Organization
    },
    {
      organizationId: string
      isSameOrganization: boolean
    },
    AsyncThunkConfig
  >(
    'INIT_ORGANIZATION',
    async ({ organizationId }, { extra, fulfillWithValue, rejectWithValue, getState }) => {
      const { api } = extra

      const user = getState().user.me
      if (!DataStatus.isFulfilled(user)) return rejectWithValue(RejectValue.NoUser)

      let organization = getCurrentOrganization(getState()) // cache
      let caseWorker = getState().user.caseWorker // cache
      let beneficiaries = getState().beneficiary.all // cache
      if (
        // Not in cache
        !DataStatus.isFulfilled(organization) ||
        !DataStatus.isFulfilled(caseWorker) ||
        !DataStatus.isFulfilled(beneficiaries) ||
        // Different organization
        organization.id !== organizationId
      ) {
        const org = await api.organizations.getOne(organizationId)
        if (hasError(org)) return rejectWithValue(handleError(org))
        organization = org

        const cw = await api.caseWorkers.getCurrent({ organizationId })
        if (hasError(cw)) return rejectWithValue(handleError(cw))
        caseWorker = cw

        const b = await api.beneficiaries.get({
          asCaseWorkerId: getCaseWorkerFilter({
            targetCaseWorkerId: caseWorker.id,
            caseWorker,
          }),
          organizationId,
        })
        if (hasError(b)) return rejectWithValue(handleError(b))
        beneficiaries = b
      }

      // TODO: use dedicated assistant action instead in order to poll alerts every 10 seconds
      const alerts = await api.alerts.get({
        asCaseWorkerId: getCaseWorkerFilter({
          targetCaseWorkerId: caseWorker.id,
          caseWorker,
        }),
        organizationId,
      })
      if (hasError(alerts)) return rejectWithValue(handleError(alerts))

      // While Onboarding
      if (
        organization.onboarding.step !== OnboardingStep.COMPLETED &&
        caseWorker.language !== organization.language
      ) {
        // FIXME: when available from backend
        //  const res = await api.me.patch({
        //   organizationId: organization.id,
        //   fields,
        // })
        const user = await api.auth.patchCurrent({
          fields: {
            language: organization.language,
          },
        })
        if (hasError(user)) return rejectWithValue(handleError(user))
        caseWorker = {
          ...caseWorker,
          language: user.language,
        }
      }

      return fulfillWithValue({
        alerts,
        beneficiaries,
        caseWorker,
        organization,
      })
    },
  ),
  login: createAsyncThunk<
    { accessToken: string },
    { email: string; password: string },
    AsyncThunkConfig
  >('LOGIN', async ({ email, password }, { extra, rejectWithValue, fulfillWithValue }) => {
    const { altApi, accessTokenManager } = extra
    const res = await altApi.auth.login({ email, password })
    if (hasError(res))
      return rejectWithValue(
        handleError(res, {
          errors: [
            {
              title: 'loginError.title',
              description: 'loginError.description',
              statusCode: 401,
            },
          ],
        }),
      )
    accessTokenManager.set(res.accessToken)
    return fulfillWithValue(res)
  }),
  logout: createAsyncThunk<void, void, AsyncThunkConfig>('LOGOUT', (_, { extra }) => {
    extra.accessTokenManager.set('null')
  }),
  task: task.app,
  undoFromNotification: createAction<{ notificationId: string }>('UNDO_FROM_NOTIFICATION'),
}

export const getCaseWorkerFilter = ({
  targetCaseWorkerId,
}: {
  targetCaseWorkerId: string
  caseWorker: AsyncData<CaseWorker>
}): string => {
  // const getAbility = ({
  //   user,
  //   caseWorker,
  // }: {
  //   user?: AsyncData<User>
  //   caseWorker?: AsyncData<CaseWorker>
  // }) => {
  //   return defineAbilitiesFor({
  //     user: DataStatus.isFulfilled(user) ? user : undefined,
  //     caseWorker: DataStatus.isFulfilled(caseWorker) ? caseWorker : undefined,
  //   })
  // }
  // const ability = getAbility({ caseWorker })
  // return ability.can('read', 'all')
  //   ? CaseWorkerIdFilter_AllBeneficiaries
  //   : targetCaseWorkerId
  return targetCaseWorkerId
}
